/**
 * TrialBanner
 * Dismissible amber warning shown during free trial period.
 * Only visible to admins when billing is enabled and a trial is active.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { AlertTriangle, X } from 'lucide-react';
import { useBilling } from '../../contexts/BillingContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../i18n';
import { ROUTES } from '../../router';

const DISMISSED_KEY = 'trial_banner_dismissed_v1';

export function TrialBanner() {
  const { trialInfo, billingVisible, isEnterprise } = useBilling();
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';

  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === 'true'
  );

  // Clear dismissal once the trial ends so the banner never shows stale state
  useEffect(() => {
    if (trialInfo && !trialInfo.is_trial) {
      localStorage.removeItem(DISMISSED_KEY);
    }
  }, [trialInfo]);

  if (
    !trialInfo?.is_trial ||
    !billingVisible ||
    isEnterprise ||
    !isAdmin ||
    dismissed
  ) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  const daysRemaining = trialInfo.days_remaining;

  return (
    <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/60 px-4 py-3 flex items-start gap-3 animate-in slide-in-from-top duration-300">
      <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
      <div className="flex-1 text-sm text-amber-800 dark:text-amber-200">
        <span className="font-semibold">{t('billing.trial_active')} — </span>
        {daysRemaining !== null
          ? t('billing.trial_ends_soon', { count: daysRemaining })
          : t('billing.trial_active')}
        {'. '}
        <button
          onClick={() => navigate(ROUTES.BILLING)}
          className="underline hover:text-amber-900 dark:hover:text-amber-100 font-medium"
        >
          {t('billing.trial_subscribe_cta')}
        </button>
      </div>
      <button
        onClick={handleDismiss}
        className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 p-0.5"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
